import React, { useContext } from 'react';
import { BrowserRouter, useInRouterContext } from 'react-router-dom';
import {
  Provider as ReduxProvider,
  ReactReduxContext,
} from 'react-redux';
import { ApiProvider } from './context/ApiContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import defaultStore from './redux/store';
import App from './App';
import './styles/index.css';

function ThemedShell({ children }) {
  const { cssVars, darkMode, mode } = useTheme();

  return (
    <div
      className={`ca-root ${darkMode ? 'ca-root--dark' : 'ca-root--light'}`}
      data-ca-theme={mode}
      style={cssVars}
    >
      {children}
    </div>
  );
}

function OptionalRouter({ basePath, children }) {
  const hasParentRouter = useInRouterContext();

  if (hasParentRouter) return children;
  return <BrowserRouter basename={basePath}>{children}</BrowserRouter>;
}

function OptionalReduxProvider({ store, children }) {
  const parentRedux = useContext(ReactReduxContext);

  // Reuse the parent's provider by default. An explicit store remains useful
  // for isolation, while the package store keeps standalone previews working.
  if (!store && parentRedux?.store) return children;
  return <ReduxProvider store={store || defaultStore}>{children}</ReduxProvider>;
}

/**
 * Root package component — mount inside the parent app.
 *
 * Theme & dark mode are parent-controlled:
 *   <ComplaintApp theme={brandTheme} darkMode={parentIsDark} />
 */
const ComplaintApp = ({
  apiUrl,
  authToken,
  store,
  basePath = '/',
  theme,
  darkMode = false,
  title,
  description,
  systems,
  systemsEndpoint,
  useDemoData = true,
  createLabel,
  onCreateForm,
  onConfigure,
  onOpen,
  onTitleClick,
  onDescriptionClick,
  onSearch,
  onPublish,
  onSaveDraft,
  showSaveButton,
  showNextButton,
  onStepChange,
  children,
}) => {
  const pageProps = {
    title,
    description,
    systems,
    systemsEndpoint,
    useDemoData,
    createLabel,
    onCreateForm,
    onConfigure,
    onOpen,
    onTitleClick,
    onDescriptionClick,
    onSearch,
    onSaveDraft,
    showSaveButton,
    showNextButton,
  };

  return (
    <ThemeProvider theme={theme} darkMode={darkMode}>
      <OptionalReduxProvider store={store}>
        <ThemedShell>
          <ApiProvider apiUrl={apiUrl} authToken={authToken}>
            <OptionalRouter basePath={basePath}>
              <App
                pageProps={pageProps}
                onPublish={onPublish}
                onStepChange={onStepChange}
              >
                {children}
              </App>
            </OptionalRouter>
          </ApiProvider>
        </ThemedShell>
      </OptionalReduxProvider>
    </ThemeProvider>
  );
};

export default ComplaintApp;
